import React, { useMemo, memo } from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { getResolvedProductDisplayImageUrl } from './productUtils';
import { ProductImageBox } from './ProductImageBox';
import { Package, Tag, Image as ImageIcon, Factory } from '@phosphor-icons/react';

const fmt = (val) => (val == null || val === '' ? '—' : val);
const hasValue = (val) => val != null && val !== '' && val !== '—';

const CELL_BASE = { fontSize: '0.78rem', whiteSpace: 'nowrap' };
const CELL_ELLIPSIS = {
  ...CELL_BASE,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export const HEAD_CELL = { fontWeight: 600, whiteSpace: 'nowrap' };

export const CATALOG_COLUMNS = [
  { key: 'EAN', label: 'EAN', width: 120 },
  { key: 'Title', label: 'Title', ellipsis: true, maxWidth: 220 },
  { key: 'brand', label: 'Brand', width: 100 },
  { key: 'status', label: 'Status', width: 80 },
  { key: 'reviewed_by', label: 'Reviewed by', width: 140, ellipsis: true },
];

/** Top-level reference_core fields shown under Core (not category-specific). */
const CORE_DETAIL_FIELDS = [
  { key: 'EAN', label: 'EAN' },
  { key: 'catalog_id', label: 'Catalog ID' },
  { key: 'model_series', label: 'Model series' },
  { key: 'portions_in_package', label: 'Units in package' },
  { key: 'status', label: 'Status' },
  { key: 'reviewed_by', label: 'Reviewed by' },
  { key: 'reviewed_at', label: 'Reviewed at' },
];

const HEADER_KEYS = new Set([
  'Title', 'brand', 'manufacturer', 'category', 'Image', 'Url', 'status',
]);
const CORE_KEYS = new Set(CORE_DETAIL_FIELDS.map((f) => f.key));

function fieldLabel(key) {
  return String(key).replace(/_/g, ' ');
}

function attributeEntries(product) {
  return Object.entries(product)
    .filter(([key, val]) => hasValue(val) && !HEADER_KEYS.has(key) && !CORE_KEYS.has(key))
    .sort(([a], [b]) => a.localeCompare(b));
}

function DetailFields({ fields }) {
  if (!fields.length) return null;
  return (
    <Grid container spacing={2}>
      {fields.map(({ key, label, value }) => (
        <Grid item xs={12} sm={6} md={4} key={key}>
          <Typography variant="body2">
            <strong>{label}:</strong> {fmt(value)}
          </Typography>
        </Grid>
      ))}
    </Grid>
  );
}

function DuplicateSection({ title, rows, columns, maxHeight, rowKey }) {
  if (!rows?.length) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {title} ({rows.length})
      </Typography>
      <TableContainer sx={{ maxHeight }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.key}>{col.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={rowKey(row)}>
                {columns.map((col) => (
                  <TableCell key={col.key} sx={col.nowrap ? { whiteSpace: 'nowrap' } : undefined}>
                    {row[col.key] ?? '—'}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export const CatalogRow = memo(function CatalogRow({ row, selected, onSelect }) {
  return (
    <TableRow
      hover
      selected={selected}
      sx={{ cursor: 'pointer' }}
      onClick={() => onSelect(row.EAN || null)}
    >
      {CATALOG_COLUMNS.map((col) => (
        <TableCell
          key={col.key}
          sx={{
            ...(col.ellipsis ? CELL_ELLIPSIS : CELL_BASE),
            width: col.width,
            maxWidth: col.maxWidth,
          }}
          title={col.ellipsis ? (row[col.key] || undefined) : undefined}
        >
          {fmt(row[col.key])}
        </TableCell>
      ))}
    </TableRow>
  );
});

export const ProductDetailPanel = memo(function ProductDetailPanel({ product, loading }) {
  const productImageSrc = useMemo(
    () => (product ? getResolvedProductDisplayImageUrl(product) : null),
    [product]
  );

  const coreFields = useMemo(() => {
    if (!product) return [];
    return CORE_DETAIL_FIELDS
      .filter(({ key }) => hasValue(product[key]))
      .map(({ key, label }) => ({ key, label, value: product[key] }));
  }, [product]);

  const attributes = useMemo(
    () => (product ? attributeEntries(product).map(([key, value]) => ({
      key,
      label: fieldLabel(key),
      value,
    })) : []),
    [product]
  );

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!product) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Select a row on the left to view product details.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Typography variant="subtitle2" gutterBottom>
            <ImageIcon size={18} style={{ marginRight: 6, verticalAlign: 'middle' }} />
            Product Image
          </Typography>
          {productImageSrc ? (
            <ProductImageBox src={productImageSrc} alt={product.Title} size={160} />
          ) : (
            <Box
              sx={{
                width: 160,
                height: 160,
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'action.hover',
              }}
            >
              <Typography variant="caption" color="text.secondary">No image</Typography>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} sm={8}>
          <Typography variant="h5" gutterBottom sx={{ color: '#1976d2' }}>
            {product.Title}
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {hasValue(product.brand) && (
              <Chip icon={<Package size={16} />} label={product.brand} color="primary" variant="outlined" />
            )}
            {hasValue(product.manufacturer) && (
              <Chip icon={<Factory size={16} />} label={product.manufacturer} color="secondary" variant="outlined" />
            )}
            {hasValue(product.category) && (
              <Chip icon={<Tag size={16} />} label={product.category} color="success" variant="outlined" />
            )}
            {hasValue(product.status) && (
              <Chip label={product.status} size="small" variant="outlined" />
            )}
          </Stack>
          {hasValue(product.Url) && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Product URL:</strong>{' '}
              <a href={product.Url} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                {product.Url}
              </a>
            </Typography>
          )}
        </Grid>
      </Grid>

      {coreFields.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>Core</Typography>
          <DetailFields fields={coreFields} />
        </Box>
      )}

      {attributes.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>Attributes</Typography>
          <DetailFields fields={attributes} />
        </Box>
      )}
    </Box>
  );
});

export function ImportDuplicatesDialog({
  preview,
  importing,
  onClose,
  onSkip,
  onOverwrite,
}) {
  return (
    <Dialog open={Boolean(preview)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Duplicate EANs in import</DialogTitle>
      <DialogContent dividers>
        {preview && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {preview.valid_rows} valid rows · {preview.new_eans} new EANs ·{' '}
              {preview.db_duplicates?.length || 0} already in catalog ·{' '}
              {preview.file_duplicates?.length || 0} repeated in file.
              Choose whether to overwrite existing catalog rows or skip them.
              Repeated rows in the file still keep the last occurrence among rows that are applied.
            </Typography>

            <DuplicateSection
              title="Already in catalog"
              rows={preview.db_duplicates}
              maxHeight={240}
              rowKey={(row) => `db-${row.ean}-${row.csv_line}`}
              columns={[
                { key: 'ean', label: 'EAN', nowrap: true },
                { key: 'csv_title', label: 'CSV title' },
                { key: 'existing_title', label: 'Existing title' },
                { key: 'csv_line', label: 'Line' },
              ]}
            />

            <DuplicateSection
              title="Repeated in file"
              rows={preview.file_duplicates}
              maxHeight={200}
              rowKey={(row) => `file-${row.ean}`}
              columns={[
                { key: 'ean', label: 'EAN', nowrap: true },
                { key: 'csv_title', label: 'Title' },
                { key: 'occurrences', label: 'Occurrences' },
                { key: 'csv_line', label: 'First line' },
              ]}
            />

            {(preview.parse_errors?.length || 0) > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {preview.parse_errors.length} row(s) failed to parse and will be skipped.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={onClose} disabled={importing}>
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" disabled={importing} onClick={onSkip}>
          Skip duplicates
        </Button>
        <Button
          variant="contained"
          disabled={importing}
          startIcon={importing ? <CircularProgress size={14} color="inherit" /> : null}
          onClick={onOverwrite}
        >
          Overwrite existing
        </Button>
      </DialogActions>
    </Dialog>
  );
}
